import React, { Suspense } from 'react';
import * as S from './configuration-rule.styles';

const LazyFunctionCodeEditor = React.lazy(() => import('./configuration-function-editor-lazy'));

export interface IConfigurationFunctionEditorProps {
    disabled?: boolean;
    onChange: (value: string) => void;
    value: string;
}

/**
 * The JS snippet a rule's EXECUTE_ENTER_FUNCTION action runs against the claim. Always editable
 * by hand, whether or not `ConfigurationFunctionBuilder` (the AI "Build the function" flow, one
 * level up in `configuration-rule-card.tsx`) is available — this box is the manual fallback and
 * the place any AI-generated code lands for review, never a black box the AI writes to directly.
 *
 * The backend already wraps the stored snippet inside `(async function (payload, payers, providers) { ... })`,
 * so what is edited here is a raw statement block — NOT a function declaration. The editor shows
 * and stores the snippet as-is; syntax validation parses it inside the same wrapper the backend
 * uses (see `checkWorkerFunctionSyntax`).
 *
 * Syntax / forbidden-pattern errors are NOT shown here — they used to be, duplicating the field
 * error `configuration-rule-card.tsx` already renders below this component (same checks, same
 * `actionValue.function`, both live on every keystroke via the observer re-render). One message,
 * in the same place every other field in this form shows its error, not two.
 */
export const ConfigurationFunctionEditor: React.FC<IConfigurationFunctionEditorProps> = ({
    disabled,
    onChange,
    value,
}) => {
    return (
        <S.FunctionEditorShell>
            <S.FunctionEditorFrame>
                <Suspense fallback={<S.FunctionEditorLoading>Loading editor…</S.FunctionEditorLoading>}>
                    <LazyFunctionCodeEditor value={value} onChange={onChange} disabled={disabled} />
                </Suspense>
            </S.FunctionEditorFrame>
        </S.FunctionEditorShell>
    );
};
